import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { z } from 'zod'

import { Bouton, Champ, Liste, Saisie } from '../components/ui/Base'
import { Bandeau, MessageErreur } from '../components/ui/Etats'
import { Squelette } from '../components/ui/Squelette'
import { useToast } from '../components/ui/Toasts'
import { api } from '../lib/api/client'
import { cles } from '../lib/api/cles'
import { ApiError } from '../lib/api/erreurs'
import type { Patient } from '../lib/api/types'
import { useSession } from '../lib/auth'

/**
 * Création et modification d'un patient (§5.5).
 *
 * L'ordre des sections n'est pas décoratif : **l'accès au domicile vient juste
 * après l'adresse**, avant le médical. C'est le même parti pris que la fiche de
 * consultation — un digicode manquant coûte dix minutes de sonnette, et si le
 * champ est enterré en bas d'un long formulaire, il n'est jamais rempli.
 */

/** "" → null. L'API distingue « non renseigné » d'une chaîne vide. */
function videEnNull(valeur: string | undefined): string | null {
  const propre = (valeur ?? '').trim()
  return propre === '' ? null : propre
}

const TELEPHONE = /^(?:\+33|0)\s?[1-9](?:[\s.-]?\d{2}){4}$/

const schema = z.object({
  nom: z.string().min(1, 'Le nom est obligatoire.').max(100),
  prenom: z.string().min(1, 'Le prénom est obligatoire.').max(100),
  date_naissance: z.string().optional(),
  sexe: z.enum(['', 'F', 'M', 'AUTRE']),
  telephone: z.string().regex(TELEPHONE, 'Numéro de téléphone invalide.').or(z.literal('')),
  telephone_secondaire: z
    .string()
    .regex(TELEPHONE, 'Numéro de téléphone invalide.')
    .or(z.literal('')),
  email: z.string().email('Adresse électronique invalide.').or(z.literal('')),
  numero_secu: z
    .string()
    .regex(/^[12]\d{14}$/, 'Le numéro de sécurité sociale comporte 15 chiffres et commence par 1 ou 2.')
    .or(z.literal('')),

  adresse: z.string().min(1, "L'adresse est obligatoire — sans elle, aucune tournée n'est calculable."),
  complement_adresse: z.string().optional(),
  code_postal: z.string().regex(/^\d{5}$/, 'Le code postal comporte 5 chiffres.'),
  ville: z.string().min(1, 'La ville est obligatoire.'),

  digicode: z.string().optional(),
  interphone: z.string().optional(),
  etage: z.string().optional(),
  ascenseur: z.enum(['', 'true', 'false']),
  acces_notes: z.string().optional(),
  presence_animaux: z.string().optional(),

  medecin_traitant: z.string().optional(),
  medecin_traitant_telephone: z
    .string()
    .regex(TELEPHONE, 'Numéro de téléphone invalide.')
    .or(z.literal('')),
  contact_urgence_nom: z.string().optional(),
  contact_urgence_telephone: z
    .string()
    .regex(TELEPHONE, 'Numéro de téléphone invalide.')
    .or(z.literal('')),
  contact_urgence_lien: z.string().optional(),
  personne_de_confiance: z.string().optional(),
  allergies: z.string().optional(),
  antecedents: z.string().optional(),
  traitements_en_cours: z.string().optional(),
  notes: z.string().optional(),
})

type Formulaire = z.infer<typeof schema>

const VALEURS_INITIALES: Formulaire = {
  nom: '',
  prenom: '',
  date_naissance: '',
  sexe: '',
  telephone: '',
  telephone_secondaire: '',
  email: '',
  numero_secu: '',
  adresse: '',
  complement_adresse: '',
  code_postal: '',
  ville: '',
  digicode: '',
  interphone: '',
  etage: '',
  ascenseur: '',
  acces_notes: '',
  presence_animaux: '',
  medecin_traitant: '',
  medecin_traitant_telephone: '',
  contact_urgence_nom: '',
  contact_urgence_telephone: '',
  contact_urgence_lien: '',
  personne_de_confiance: '',
  allergies: '',
  antecedents: '',
  traitements_en_cours: '',
  notes: '',
}

/** Réponse de création : le doublon éventuel remonte en avertissement, pas en erreur. */
interface PatientCree extends Patient {
  avertissements?: Array<{ code: string; message: string; patient_id: string }>
}

export function PatientFormulaire() {
  const { id } = useParams()
  const modification = Boolean(id)
  const naviguer = useNavigate()
  const queryClient = useQueryClient()
  const toast = useToast()
  const { estTitulaire } = useSession()

  const requete = useQuery({
    queryKey: cles.patients.detail(id ?? ''),
    queryFn: ({ signal }) => api.get<Patient>(`/patients/${id}`, signal),
    enabled: modification,
  })

  const { register, handleSubmit, reset, formState, setError } = useForm<Formulaire>({
    resolver: zodResolver(schema),
    defaultValues: VALEURS_INITIALES,
  })

  useEffect(() => {
    const p = requete.data
    if (!p) return
    reset({
      ...VALEURS_INITIALES,
      nom: p.nom,
      prenom: p.prenom,
      date_naissance: p.date_naissance ?? '',
      sexe: p.sexe ?? '',
      telephone: p.telephone ?? '',
      telephone_secondaire: p.telephone_secondaire ?? '',
      email: p.email ?? '',
      // Masqué hors TITULAIRE (R6) : le champ arrive à `null` et le formulaire
      // ne l'affiche pas. Voir le commentaire de `corpsAEnvoyer`.
      numero_secu: p.numero_secu ?? '',
      adresse: p.adresse,
      complement_adresse: p.complement_adresse ?? '',
      code_postal: p.code_postal,
      ville: p.ville,
      digicode: p.digicode ?? '',
      interphone: p.interphone ?? '',
      etage: p.etage ?? '',
      ascenseur: p.ascenseur === null ? '' : p.ascenseur ? 'true' : 'false',
      acces_notes: p.acces_notes ?? '',
      presence_animaux: p.presence_animaux ?? '',
      medecin_traitant: p.medecin_traitant ?? '',
      medecin_traitant_telephone: p.medecin_traitant_telephone ?? '',
      contact_urgence_nom: p.contact_urgence_nom ?? '',
      contact_urgence_telephone: p.contact_urgence_telephone ?? '',
      contact_urgence_lien: p.contact_urgence_lien ?? '',
      personne_de_confiance: p.personne_de_confiance ?? '',
      allergies: p.allergies ?? '',
      antecedents: p.antecedents ?? '',
      traitements_en_cours: p.traitements_en_cours ?? '',
      notes: p.notes ?? '',
    })
  }, [requete.data, reset])

  function corpsAEnvoyer(v: Formulaire): Record<string, unknown> {
    const corps: Record<string, unknown> = {
      nom: v.nom.trim(),
      prenom: v.prenom.trim(),
      date_naissance: videEnNull(v.date_naissance),
      sexe: v.sexe === '' ? null : v.sexe,
      telephone: videEnNull(v.telephone),
      telephone_secondaire: videEnNull(v.telephone_secondaire),
      email: videEnNull(v.email),
      adresse: v.adresse.trim(),
      complement_adresse: videEnNull(v.complement_adresse),
      code_postal: v.code_postal.trim(),
      ville: v.ville.trim(),
      digicode: videEnNull(v.digicode),
      interphone: videEnNull(v.interphone),
      etage: videEnNull(v.etage),
      ascenseur: v.ascenseur === '' ? null : v.ascenseur === 'true',
      acces_notes: videEnNull(v.acces_notes),
      presence_animaux: videEnNull(v.presence_animaux),
      medecin_traitant: videEnNull(v.medecin_traitant),
      medecin_traitant_telephone: videEnNull(v.medecin_traitant_telephone),
      contact_urgence_nom: videEnNull(v.contact_urgence_nom),
      contact_urgence_telephone: videEnNull(v.contact_urgence_telephone),
      contact_urgence_lien: videEnNull(v.contact_urgence_lien),
      personne_de_confiance: videEnNull(v.personne_de_confiance),
      allergies: videEnNull(v.allergies),
      antecedents: videEnNull(v.antecedents),
      traitements_en_cours: videEnNull(v.traitements_en_cours),
      notes: videEnNull(v.notes),
    }

    // ========================================================================
    // LE NUMÉRO DE SÉCURITÉ SOCIALE N'EST ENVOYÉ QUE SI ON A PU LE VOIR.
    //
    // Hors TITULAIRE, `GET /patients/{id}` le renvoie masqué, donc `null`
    // (R6). Le réémettre tel quel en PATCH effacerait une donnée qu'on n'a
    // jamais eu le droit de lire — un collaborateur corrigeant un digicode
    // supprimerait au passage le NIR du patient, sans que rien ne le signale.
    // ========================================================================
    if (!modification || estTitulaire) {
      corps.numero_secu = videEnNull(v.numero_secu)
    }
    return corps
  }

  const enregistrer = useMutation({
    mutationFn: (v: Formulaire) =>
      modification
        ? api.patch<PatientCree>(`/patients/${id}`, corpsAEnvoyer(v))
        : api.post<PatientCree>('/patients', corpsAEnvoyer(v)),
    onSuccess: (patient) => {
      // Le doublon **avertit sans bloquer** : deux patients de mêmes nom et
      // prénom, c'est une mère et sa fille, un couple âgé, un EHPAD entier.
      for (const a of patient.avertissements ?? []) {
        toast(a.message, 'info')
      }
      toast(modification ? 'Patient modifié.' : 'Patient créé.', 'succes')
      void queryClient.invalidateQueries({ queryKey: cles.patients.tout })
      naviguer(`/patients/${patient.id}`)
    },
    onError: (erreur) => {
      if (erreur instanceof ApiError && erreur.champ && erreur.champ in VALEURS_INITIALES) {
        setError(erreur.champ as keyof Formulaire, { message: erreur.message })
        return
      }
      toast(erreur instanceof Error ? erreur.message : 'Enregistrement impossible.', 'erreur')
    },
  })

  if (modification && requete.isPending) return <Squelette lignes={12} />
  if (modification && requete.isError) {
    return <MessageErreur erreur={requete.error} onReessayer={() => requete.refetch()} />
  }

  const e = formState.errors

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div>
        <Link to={modification ? `/patients/${id}` : '/patients'} className="text-sm text-sky-700 hover:underline">
          ← Retour
        </Link>
        <h1 className="mt-1 text-xl font-semibold text-slate-900">
          {modification ? `Modifier ${requete.data?.prenom} ${requete.data?.nom}` : 'Nouveau patient'}
        </h1>
      </div>

      <form
        onSubmit={handleSubmit((v) => enregistrer.mutate(v))}
        className="space-y-6 rounded-lg border border-slate-200 bg-white p-5"
        noValidate
      >
        {/* ── Identité ── */}
        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Identité</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Champ libelle="Nom" id="nom" erreur={e.nom?.message} obligatoire>
              <Saisie id="nom" autoComplete="family-name" {...register('nom')} />
            </Champ>
            <Champ libelle="Prénom" id="prenom" erreur={e.prenom?.message} obligatoire>
              <Saisie id="prenom" autoComplete="given-name" {...register('prenom')} />
            </Champ>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <Champ libelle="Date de naissance" id="naissance" erreur={e.date_naissance?.message}>
              <Saisie id="naissance" type="date" {...register('date_naissance')} />
            </Champ>
            <Champ libelle="Sexe" id="sexe">
              <Liste id="sexe" {...register('sexe')}>
                <option value="">Non précisé</option>
                <option value="F">Féminin</option>
                <option value="M">Masculin</option>
                <option value="AUTRE">Autre</option>
              </Liste>
            </Champ>
            <Champ libelle="Téléphone" id="tel" erreur={e.telephone?.message}>
              <Saisie id="tel" type="tel" autoComplete="tel" {...register('telephone')} />
            </Champ>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Champ libelle="Téléphone secondaire" id="tel2" erreur={e.telephone_secondaire?.message}>
              <Saisie id="tel2" type="tel" {...register('telephone_secondaire')} />
            </Champ>
            <Champ libelle="Courriel" id="email" erreur={e.email?.message}>
              <Saisie id="email" type="email" {...register('email')} />
            </Champ>
          </div>

          {!modification || estTitulaire ? (
            <Champ
              libelle="Numéro de sécurité sociale"
              id="secu"
              erreur={e.numero_secu?.message}
              aide="15 chiffres. Chiffré au repos ; tout accès est journalisé."
            >
              <Saisie id="secu" inputMode="numeric" autoComplete="off" {...register('numero_secu')} />
            </Champ>
          ) : (
            <Bandeau ton="info">
              Le numéro de sécurité sociale n&apos;est modifiable que par le titulaire du
              cabinet. Il reste inchangé par cet enregistrement.
            </Bandeau>
          )}
        </section>

        {/* ── Adresse ── */}
        <section className="space-y-4 border-t border-slate-100 pt-5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Adresse</h2>
          <Champ libelle="Adresse" id="adresse" erreur={e.adresse?.message} obligatoire>
            <Saisie id="adresse" autoComplete="street-address" {...register('adresse')} />
          </Champ>
          <div className="grid gap-4 sm:grid-cols-3">
            <Champ libelle="Complément" id="complement" aide="Bâtiment, résidence…">
              <Saisie id="complement" {...register('complement_adresse')} />
            </Champ>
            <Champ libelle="Code postal" id="cp" erreur={e.code_postal?.message} obligatoire>
              <Saisie id="cp" inputMode="numeric" {...register('code_postal')} />
            </Champ>
            <Champ libelle="Ville" id="ville" erreur={e.ville?.message} obligatoire>
              <Saisie id="ville" {...register('ville')} />
            </Champ>
          </div>
          <Bandeau ton="info">
            L&apos;adresse part au géocodage dès l&apos;enregistrement. Tant qu&apos;elle
            n&apos;est pas localisée, le patient reste hors des calculs de tournée.
          </Bandeau>
        </section>

        {/* ==================================================================
            ACCÈS AU DOMICILE — juste après l'adresse, jamais en fin de page.
            Le §5.5 en fait un bloc de premier plan : la fiche patient l'affiche
            au-dessus du pli, le formulaire le demande au même rang.
            ================================================================== */}
        <section className="space-y-4 border-t border-slate-100 pt-5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Accès au domicile
          </h2>
          <div className="grid gap-4 sm:grid-cols-4">
            <Champ libelle="Digicode" id="digicode">
              <Saisie id="digicode" className="font-mono" {...register('digicode')} />
            </Champ>
            <Champ libelle="Interphone" id="interphone" aide="Nom sur la platine">
              <Saisie id="interphone" {...register('interphone')} />
            </Champ>
            <Champ libelle="Étage" id="etage">
              <Saisie id="etage" {...register('etage')} />
            </Champ>
            <Champ libelle="Ascenseur" id="ascenseur">
              <Liste id="ascenseur" {...register('ascenseur')}>
                <option value="">Non précisé</option>
                <option value="true">Oui</option>
                <option value="false">Non</option>
              </Liste>
            </Champ>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Champ libelle="Notes d'accès" id="acces" aide="Porte au fond du couloir, clé chez la voisine…">
              <Saisie id="acces" {...register('acces_notes')} />
            </Champ>
            <Champ
              libelle="Animaux"
              id="animaux"
              aide="Signalé en rouge sur la fiche — un chien mordeur se sait avant d'entrer."
            >
              <Saisie id="animaux" {...register('presence_animaux')} />
            </Champ>
          </div>
        </section>

        {/* ── Médical et contacts ── */}
        <section className="space-y-4 border-t border-slate-100 pt-5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Médical et contacts
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Champ libelle="Médecin traitant" id="medecin">
              <Saisie id="medecin" {...register('medecin_traitant')} />
            </Champ>
            <Champ
              libelle="Téléphone du médecin"
              id="medecin-tel"
              erreur={e.medecin_traitant_telephone?.message}
            >
              <Saisie id="medecin-tel" type="tel" {...register('medecin_traitant_telephone')} />
            </Champ>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <Champ libelle="Contact d'urgence" id="urgence-nom">
              <Saisie id="urgence-nom" {...register('contact_urgence_nom')} />
            </Champ>
            <Champ
              libelle="Téléphone d'urgence"
              id="urgence-tel"
              erreur={e.contact_urgence_telephone?.message}
            >
              <Saisie id="urgence-tel" type="tel" {...register('contact_urgence_telephone')} />
            </Champ>
            <Champ libelle="Lien" id="urgence-lien" aide="Fille, voisin, tuteur…">
              <Saisie id="urgence-lien" {...register('contact_urgence_lien')} />
            </Champ>
          </div>
          <Champ libelle="Personne de confiance" id="confiance">
            <Saisie id="confiance" {...register('personne_de_confiance')} />
          </Champ>
          <div className="grid gap-4 sm:grid-cols-3">
            <Champ libelle="Allergies" id="allergies">
              <Saisie id="allergies" {...register('allergies')} />
            </Champ>
            <Champ libelle="Antécédents" id="antecedents">
              <Saisie id="antecedents" {...register('antecedents')} />
            </Champ>
            <Champ libelle="Traitements en cours" id="traitements">
              <Saisie id="traitements" {...register('traitements_en_cours')} />
            </Champ>
          </div>
          <Champ libelle="Notes" id="notes">
            <Saisie id="notes" {...register('notes')} />
          </Champ>
        </section>

        <div className="flex justify-end gap-2 border-t border-slate-100 pt-5">
          <Bouton
            variante="secondaire"
            type="button"
            onClick={() => naviguer(modification ? `/patients/${id}` : '/patients')}
          >
            Annuler
          </Bouton>
          <Bouton type="submit" disabled={enregistrer.isPending}>
            {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </Bouton>
        </div>
      </form>
    </div>
  )
}
